import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const CreativeSwapPerformanceChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, events, currencySettings, isLoading } = useBoardChartData();
    const currencySymbol = getCurrencySymbol(currencySettings?.default);

    const [selectedMetric, setSelectedMetric] = useState<'ctr' | 'cpc' | 'roas'>('ctr');

    const chartData = useMemo(() => {
        if (!events || !finance?.dailyBreakdown) return [];

        const creativeEvents = events.filter((e: any) => (e.type || e.eventType) === 'update_ad_creative');
        if (creativeEvents.length === 0) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();
        const results: { name: string; before: number; after: number; lift: number }[] = [];

        creativeEvents.slice(0, 6).forEach((event: any) => {
            const eventDate = event.date?.split('T')[0];
            if (!eventDate) return;
            const dateIdx = sortedDates.indexOf(eventDate);
            if (dateIdx < 0) return;

            const windowSize = 7;
            const beforeDays = sortedDates.slice(Math.max(0, dateIdx - windowSize), dateIdx);
            const afterDays = sortedDates.slice(dateIdx, Math.min(sortedDates.length, dateIdx + windowSize));

            const calculateMetric = (days: string[]) => {
                let totalClicks = 0, totalImpressions = 0, totalSpend = 0, totalRevenue = 0;
                days.forEach(d => {
                    const m = finance.dailyBreakdown![d]?.metrics;
                    totalClicks += m?.adClicks || 0;
                    totalImpressions += m?.adImpressions || 0;
                    totalSpend += m?.adSpend || 0;
                    totalRevenue += m?.revenue || 0;
                });

                if (selectedMetric === 'ctr') {
                    return totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
                } else if (selectedMetric === 'cpc') {
                    return totalClicks > 0 ? totalSpend / totalClicks : 0;
                } else if (selectedMetric === 'roas') {
                    return totalSpend > 0 ? totalRevenue / totalSpend : 0;
                }
                return 0;
            };

            const valBefore = calculateMetric(beforeDays);
            const valAfter = calculateMetric(afterDays);
            const lift = valBefore > 0 ? ((valAfter - valBefore) / valBefore) * 100 : 0;

            const label = event.description || format(parseISO(eventDate), 'MMM dd');
            results.push({
                name: label.length > 15 ? label.substring(0, 15) + '...' : label,
                before: Number(valBefore.toFixed(selectedMetric === 'ctr' ? 2 : 2)),
                after: Number(valAfter.toFixed(selectedMetric === 'ctr' ? 2 : 2)),
                lift: Number(lift.toFixed(1))
            });
        });

        return results;
    }, [events, finance, selectedMetric]);

    const isEmpty = !isLoading && chartData.length === 0;
    
    const avgLift = useMemo(() => {
        if (chartData.length === 0) return 0;
        return chartData.reduce((acc, curr) => acc + curr.lift, 0) / chartData.length;
    }, [chartData]);

    useSetChartMetadata({ 
        isEmpty,
        summary: !isEmpty ? (
            <span className={`text-[11px] font-bold ${avgLift >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {avgLift >= 0 ? '+' : ''}{avgLift.toFixed(1)}% {t('charts.components.lift')}
            </span>
        ) : undefined
    });

    const renderCustomizedLabel = (props: any) => {
        const { x, y, width, index } = props;
        const lift = chartData[index]?.lift;
        if (lift === undefined) return <g />;

        const isPositive = lift >= 0;
        const color = isPositive ? CHART_COLORS.revenue : CHART_COLORS.danger;
        
        return (
            <g>
                <text 
                    x={x + width / 2} 
                    y={y - 10} 
                    fill={color} 
                    textAnchor="middle" 
                    fontSize={10} 
                    fontWeight="bold"
                >
                    {isPositive ? '+' : ''}{lift}%
                </text>
            </g>
        );
    };

    const metricFormatter = (value: number) => {
        if (selectedMetric === 'ctr') return `${value}%`;
        if (selectedMetric === 'cpc') return `${currencySymbol}${value.toFixed(2)}`;
        if (selectedMetric === 'roas') return `${value.toFixed(2)}x`;
        return value.toString();
    };

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="px-4 py-2 flex items-center justify-center gap-2 border-b border-gray-100/50">
                {[
                    { id: 'ctr', label: t('charts.components.metricCTR') },
                    { id: 'cpc', label: t('charts.components.metricCPC') },
                    { id: 'roas', label: t('charts.components.metricROAS') }
                ].map((m) => (
                    <button
                        key={m.id}
                        onClick={() => setSelectedMetric(m.id as any)}
                        className={`px-3 py-1 text-[10px] font-medium rounded-full transition-all ${
                            selectedMetric === m.id
                                ? 'bg-primary/10 text-primary shadow-sm ring-1 ring-primary/20'
                                : 'text-gray-500 hover:bg-gray-100'
                        }`}
                    >
                        {m.label}
                    </button>
                ))}
            </div>

            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ ...CHART_STYLES.margin.withAxis, top: 25 }}>
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} vertical={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis dataKey="name" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 9 }} interval={0} />
                        <YAxis 
                            {...CHART_STYLES.axis} 
                            tickFormatter={metricFormatter} 
                            tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} 
                            width={50} 
                        />
                        <Tooltip 
                            contentStyle={CHART_STYLES.tooltip.contentStyle} 
                            formatter={(v: number) => [metricFormatter(v), selectedMetric.toUpperCase()]} 
                        />
                        <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
                        <Bar 
                            dataKey="before" 
                            name={`${selectedMetric.toUpperCase()} ${t('charts.components.before')}`} 
                            fill={CHART_COLORS.gray[300]} 
                            radius={[3, 3, 0, 0]} 
                            maxBarSize={25} 
                        />
                        <Bar 
                            dataKey="after" 
                            name={`${selectedMetric.toUpperCase()} ${t('charts.components.after')}`} 
                            fill={CHART_COLORS.chart.line4} 
                            radius={[3, 3, 0, 0]} 
                            maxBarSize={25}
                            label={renderCustomizedLabel}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
